import { redirect, useRouteLoaderData } from 'react-router';
import { storefrontContext } from '~/domain/core/index.server';
import { resolveCheckoutExperience } from '../checkout-loader-functions';
import { buildCheckoutUrl } from '~/domain/connect/deep-link';
import {
  isShared,
  resolveConnectedStorefront,
} from '~/domain/connect/index.server';
import { env } from '~/platform/platform.server';
import { loggerContext } from '~/lib/logger';
import { useLocaleHref } from '~/lib/use-locale-href';
import type { loader as experienceLoader } from '~/routes/experience/route';
import type { Route } from './+types/experience-route';
import { continueBooking, loadBookingDialogDeferred } from './load.server';
import { parseSelectionFromForm } from './parse-selection.server';
import { BookingDialogRoute } from './booking-dialog-route';

export function loader({ request, url, context, params }: Route.LoaderArgs) {
  // Nothing is awaited: sync fields open the dialog (with a live date
  // stepper) instantly, while the `tickets` and `events` promises stream in
  // behind per-section loading states (see BookingDialogRoute).
  return loadBookingDialogDeferred({ request, url, context, params });
}

export async function action({
  request,
  url,
  context,
  params,
}: Route.ActionArgs) {
  const logger = context.get(loggerContext);
  const checkoutExperience = await resolveCheckoutExperience({
    params,
    storefront: context.get(storefrontContext),
    logger,
  });
  const expId = params.experienceId;
  const { experience } = checkoutExperience;

  // Shared (connected) experiences are hosted by another company. The
  // visitor is on the distributor's storefront; checkout must happen on the
  // owner's storefront so the basket, payments, and post-purchase emails
  // live where the experience does. `buildCheckoutUrl` handles the
  // cross-domain URL + `distributorId` param + same-domain fallback when
  // the owner FQDN can't be resolved, and (via `selection`) carries the
  // dialog's pick over so the owner storefront can pre-fill it.
  if (isShared(experience)) {
    const owner = await resolveConnectedStorefront(experience.hostId, {
      experienceId: experience.ownerExperienceId,
      logger,
    });
    const target = buildCheckoutUrl({
      experience,
      ownerFqdn: owner?.fqdn ?? null,
      locale: params.locale,
      currentUrl: url,
      rootDomain: env.ROOT_DOMAIN ?? '',
      selection: await parseSelectionFromForm(request, checkoutExperience),
    });
    return redirect(target);
  }

  return continueBooking({ request, url, params }, expId, checkoutExperience);
}

export default function ExperienceBookingRoute({
  loaderData,
  params,
}: Route.ComponentProps) {
  const localeHref = useLocaleHref();
  const experience = useRouteLoaderData<typeof experienceLoader>(
    'routes/experience/route'
  );
  return (
    <BookingDialogRoute
      loaderData={loaderData}
      closeTo={localeHref('/:locale?/experience/:experienceId', {
        experienceId: params.experienceId,
      })}
      bookingMode={experience?.bookingWidgetMode}
    />
  );
}
